import React, { useEffect, useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';

/**
 * Public-facing security notice — informs visitors that DocSite is only
 * distributed via docsite.care and warns about repackaged APKs / installers
 * that may contain malware.
 *
 * Behaviour: visible for the first 4 seconds of every page load, then
 * smoothly collapses (max-height + opacity transition) so the page returns
 * to its regular layout. The notice still exists in the DOM (zero shift),
 * just becomes 0-height and inert.
 */
export default function SecurityNoticeBanner() {
  const [visible, setVisible] = useState(true);
  const [maxH, setMaxH] = useState('1000px');
  const wrapperRef = useRef(null);

  useEffect(() => {
    // On first paint, force the viewport to the very top so a cached/restored
    // scroll position from a prior visit doesn't leave the banner partially or
    // fully clipped (Chrome restores scroll on reload by default). Using
    // `instant` so the user doesn't see a smooth-scroll surprise.
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }
    catch { window.scrollTo(0, 0); }

    // Pin actual rendered height before collapse so the transition animates
    // smoothly (max-height: auto isn't transitionable).
    const measureAndStart = () => {
      if (wrapperRef.current) {
        const h = wrapperRef.current.scrollHeight;
        setMaxH(`${h}px`);
      }
    };
    measureAndStart();
    const t = setTimeout(() => {
      // Re-measure right before collapse in case fonts/layout settled late.
      if (wrapperRef.current) {
        setMaxH(`${wrapperRef.current.scrollHeight}px`);
      }
      // Next tick → trigger transition to 0.
      requestAnimationFrame(() => setVisible(false));
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid="home-security-notice"
      aria-hidden={!visible}
      style={{
        maxHeight: visible ? maxH : '0px',
        opacity: visible ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height 700ms cubic-bezier(.4,0,.2,1), opacity 500ms ease-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <section className="py-6 md:py-8 bg-white">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-rose-50 p-6 md:p-7 max-w-4xl flex items-start gap-4 md:gap-5">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <ShieldAlert size={28} strokeWidth={2.4} className="text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm uppercase tracking-[0.2em] font-extrabold text-rose-600 mb-2" data-testid="security-notice-tag">
                Important · Security Notice
              </p>
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                DocSite is officially distributed only from{' '}
                <a href="https://docsite.care" className="text-amber-700 underline underline-offset-2 hover:text-amber-800">docsite.care</a>.
              </p>
              <p className="text-base text-slate-700 mt-3 leading-relaxed">
                Any DocSite app, APK, or installer received via <b>WhatsApp, Telegram, forums, or file-sharing sites</b> is <b>not from us</b> and is likely repackaged with malware. Please install only via the "Install app" button on docsite.care, or from the official Play Store / App Store listing once available.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
